'use client';

import React, { useEffect, useRef, useState } from 'react';
import SpeechRecognition, {
  useSpeechRecognition,
} from 'react-speech-recognition';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';

import Deacuerdo from '../../../../assets/icons/deacuerdo.svg';
import EnDesacuerdo from '../../../../assets/icons/en desacuerdo.svg';
import NiDeacuerdoNiEnDesacuerdo from '../../../../assets/icons/ni deacuerdo ni en desacuerdo.svg';
import TotalmenteDeAcuerdo from '../../../../assets/icons/totalmente de acuerdo.svg';
import TotalmenteEnDesacuerdo from '../../../../assets/icons/totalmente en desacuerdo.svg';
import { uploadAllSurveyFiles } from '../../services/fileUploadService';
import {
  buildDemographicAnswers,
  getMaxDepth,
  validateAllDemographics,
} from '../../utils/demographicUtils';
import {
  isBigSelection,
  isBipolarSlider,
  isCheckbox,
  isComplex,
  isConstantAdd,
  isInformativeText,
  isMatrix,
  isMultimedia,
  isRelational,
  isTable,
  isText,
} from '../../utils/questionTypeUtils';
import { useDictation } from '../../utils/useDictation';
import { validateInputFormat } from '../../utils/validateInputFormat';
import DemographicSection from '../DemographicSection/DemographicSection';
import { useNavigationHandler } from '../hooks/useNavigationHandler';
import { usePausedDataLoader } from '../hooks/usePausedDataLoader';
import PauseButton from '../PauseButton/PauseButton';
import QuestionRenderer from '../Questions/QuestionRenderer/QuestionRenderer';
import SurveyDescription from '../SurveyDescription/SurveyDescription';
import SurveyNavigation from '../SurveyNavigation/SurveyNavigation';

import styles from './SurveyForm.module.css';
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};
if (typeof window !== 'undefined') {
  if (sessionStorage.getItem('hasVisited')) {
    // Esto significa que la página se recargó
    sessionStorage.removeItem('demographicSelections');
    sessionStorage.removeItem('formValues'); // ✅ AGREGAR ESTA LÍNEA
  }

  // Marca que ya entró una vez
  sessionStorage.setItem('hasVisited', 'true');
}

if (
  typeof window !== 'undefined' &&
  performance.getEntriesByType('navigation')[0]?.type === 'reload'
) {
  sessionStorage.removeItem('demographicSelections');
  sessionStorage.removeItem('formValues'); // ✅ AGREGAR ESTA LÍNEA
}
const SurveyForm = ({
  questions = [],
  pagination,
  onAnswered,
  companyId,
  nameStep,
  activeStepper,
  surveyId,
  handleNextAnswer,
  descriptionSurvey,
  primaryColor,
  secondaryColor,
  isSaveOption,
  saveSurvey,
  data,
  demographicData,
  fontFamily,
  handlePreviousAnswer,
  answerId,
  hasOwnBold,
  initialDemographicSelections,
}) => {
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();
  // Estado para controlar si ya se cargaron los datos pausados
  //const [isPausedDataLoaded, setIsPausedDataLoaded] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [visibleQuestions, setVisibleQuestions] = useState(
    questions.map(() => true),
  );
  const matrizRefs = useRef({});

  const initializeFormValue = (question) => {
    const base = {
      id: question.questionId,
      questionType: question.typeQuestion,
      value: '',
      values: {},
      selectionValues: {},
    };
    if (
      !isInformativeText(question.typeQuestion) &&
      !isMultimedia(question.typeQuestion)
    ) {
      base.nameQuestion = question.questionName;
    }

    if (isConstantAdd(question.typeQuestion)) {
      const sliders = {};
      const values = {};

      question.options.forEach((option) => {
        sliders[option.optionName] = 0;
        values[option.optionName] = false;
      });

      return {
        ...base,
        sliders,
        values,
      };
    }

    // Agregar inicialización para preguntas compuestas
    if (isComplex(question.typeQuestion)) {
      return {
        ...base,
        complexAnswers: [],
      };
    }

    return base;
  };

  const [formValues, setFormValues] = useState(() => {
    // Intentar restaurar desde sessionStorage
    const saved = sessionStorage.getItem('formValues');
    if (saved && saved !== '[]' && saved !== '{}') {
      // ✅ Verificar que no esté vacío
      try {
        const parsed = JSON.parse(saved);
        // ✅ Verificar que sea un array válido con datos
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      } catch (error) {
        console.error('Error parsing formValues from sessionStorage:', error);
      }
    }

    // Si no hay nada válido guardado, inicializar normalmente
    return questions.map((question) => initializeFormValue(question));
  });
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const formValuesRef = useRef(formValues);

  const [isZeroIndexActive, setIsZeroIndexActive] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);
  const [values, setValues] = useState({});
  const isMobile = useIsMobile();
  const [verMas, setVerMas] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [sliderErrors, setSliderErrors] = useState({});
  const [otherOptionValues, setOtherOptionValues] = useState({});
  const [selectedRadioOptions, setSelectedRadioOptions] = useState({});
  const textoSinBr = descriptionSurvey
    ? descriptionSurvey.replace(/<br\/>/g, '\n')
    : '';
  const textoAMostrar =
    !isMobile || verMas || !descriptionSurvey
      ? textoSinBr
      : textoSinBr.length > 150
        ? `${textoSinBr.substring(0, 150)}...`
        : textoSinBr;

  const [questionRefs, setQuestionRefs] = useState({});
  const [demographicSelections, setDemographicSelections] = useState(() => {
    const saved = sessionStorage.getItem('demographicSelections');
    return saved ? JSON.parse(saved) : {};
  });

  // Al retomar una encuesta pausada, rellenar con lo que quedó guardado en el
  // servidor. sessionStorage solo sobrevive mientras la pestaña siga abierta,
  // así que quien volvía al otro día encontraba el formulario vacío.
  // No pisa lo que haya en pantalla: si ya hay selecciones -porque la pestaña
  // nunca se cerró o porque la persona acaba de tocar algo- mandan esas.
  useEffect(() => {
    if (
      !initialDemographicSelections ||
      Object.keys(initialDemographicSelections).length === 0
    ) {
      return;
    }
    setDemographicSelections((previas) =>
      Object.keys(previas).length > 0 ? previas : initialDemographicSelections,
    );
  }, [initialDemographicSelections]);
  const [activeDemographicStep, setActiveDemographicStep] = useState(0);
  const MAX_RECORDING_TIME = 60; // 60 segundos
  const MAX_RESETS = 2; // Máximo 2 resets

  const normalizedDemographicData = React.useMemo(() => {
    if (!demographicData) return [];

    return demographicData.map((demo) => ({
      demographicName: demo.name || demo.demographicName,
      levelNames: demo.levelNames,
      nodes: demo.nodes,
      allowMultipleSelection: demo.allowMultipleSelection,
    }));
  }, [demographicData]);
  const totalDemographicSteps = () => {
    if (!normalizedDemographicData || normalizedDemographicData.length === 0)
      return 0; // ✅ USAR normalizedDemographicData
    return Math.ceil(normalizedDemographicData.length / pagination); // ✅ USAR normalizedDemographicData
  };
  const handleNext = async () => {
    setSubmitAttempted(true);
    // ✅ VALIDACIÓN ESPECIAL PARA DEMOGRAFÍA
    if (nameStep && nameStep[activeStepper] === 'Datos demográficos') {
      const startIdx = activeDemographicStep * pagination;
      const endIdx = (activeDemographicStep + 1) * pagination;
      const currentDemographics = normalizedDemographicData.slice(
        startIdx,
        endIdx,
      );

      // ✅ Validación simplificada
      const allDemographicsValid = validateAllDemographics(
        currentDemographics,
        demographicSelections,
      );

      if (!allDemographicsValid) {
        return;
      }

      setSubmitAttempted(false);

      if (activeDemographicStep + 1 < totalDemographicSteps()) {
        setActiveDemographicStep((prev) => prev + 1);
        return;
      }

      // ✅ Construcción de respuestas simplificada
      const demographicAnswers = buildDemographicAnswers(
        demographicSelections,
        normalizedDemographicData,
      );

      onAnswered(demographicAnswers);
      setActiveDemographicStep(0);
      setActiveStep(0);
      handleNextAnswer();
      return;
    }
    const errors = {};
    let firstErrorIndex = null;

    formValues.forEach((formValue, idx) => {
      const question = questions[idx]; // ← AGREGAR ESTA LÍNEA
      if (idx < activeStep * pagination || idx >= (activeStep + 1) * pagination)
        return;
      if (!visibleQuestions[idx]) return;
      if (isConstantAdd(question?.typeQuestion) && !question?.autoValidate) {
        const sum = Object.values(formValue.sliders || {}).reduce(
          (a, b) => a + b,
          0,
        );
        const max = question?.score ?? 100;
        /*if (sum > max) {
          errors[
            idx
          ] = `Te has excedido del límite permitido. Por favor, ajusta los valores para no superar ${max} puntos.`;
          if (firstErrorIndex === null) firstErrorIndex = idx;
        }*/
        if (sum > max) {
          errors[idx] =
            `Te has excedido del límite permitido. Por favor, ajusta los valores para no superar ${max} puntos.`;
          if (firstErrorIndex === null) firstErrorIndex = idx;
        } else if (sum < max) {
          errors[idx] =
            `Debes distribuir exactamente ${max} puntos. Te faltan ${max - sum} puntos por asignar.`;
          if (firstErrorIndex === null) firstErrorIndex = idx;
        }
      }
      if (isText(question?.typeQuestion) && question?.required) {
        const answer = formValue.value;
        const validationType = question?.settings?.textType || 'text';

        if (answer && answer.trim() !== '') {
          // Si hay respuesta, validar el formato
          if (!validateInputFormat(answer, validationType)) {
            if (validationType === 'email') {
              errors[idx] = 'Por favor, ingrese un correo electrónico válido';
            } else if (validationType === 'number') {
              errors[idx] = 'Por favor, ingrese solo números';
            }

            if (firstErrorIndex === null) firstErrorIndex = idx;
          }
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setSliderErrors(errors);
      verifyCurrentStepAnswersSelected();
      // Hacer scroll a la primera pregunta con error
      if (firstErrorIndex !== null) {
        const questionId = questions[firstErrorIndex].questionId;
        questionRefs[questionId]?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      return;
    }

    setSliderErrors({});
    if (verifyCurrentStepAnswersSelected()) {
      if (activeStep + 1 !== totalOfSteps()) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        return;
      }

      try {
        setIsUploadingFiles(true);

        const result = await uploadAllSurveyFiles(
          surveyId,
          answerId,
          formValues,
          questions,
          questionRefs,
        );

        if (!result.success) {
          // ✅ Mostrar error principal
          enqueueSnackbar(result.error || 'Error al subir archivos', {
            variant: 'error',
            autoHideDuration: 5000,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
          });

          // ✅ Si hay errores detallados, mostrarlos uno por uno
          if (
            result.errors &&
            Array.isArray(result.errors) &&
            result.errors.length > 0
          ) {
            result.errors.forEach((err, index) => {
              setTimeout(() => {
                let message = '';

                if (err.FileName) {
                  message = `${err.FileName}: ${err.Error}`;
                } else if (err.QuestionId) {
                  const question = questions.find(
                    (q) => q.questionId === err.QuestionId,
                  );
                  const questionName =
                    question?.questionName || `Pregunta ${err.QuestionId}`;
                  message = `${questionName}: ${err.Error}`;
                } else {
                  message = err.Error || JSON.stringify(err);
                }

                enqueueSnackbar(message, {
                  variant: 'warning',
                  autoHideDuration: 6000,
                  anchorOrigin: { vertical: 'top', horizontal: 'right' },
                });
              }, index * 300); // Mostrar con un pequeño delay entre cada uno
            });
          }

          setIsUploadingFiles(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        let updatedFormValues = formValues;

        if (result.data?.questions?.length > 0) {
          updatedFormValues = formValues.map((fv) => {
            const question = questions.find((q) => q.questionId === fv.id);
            if (!question) return fv;

            if (question.typeQuestion === 'Archivos') {
              const uploadedQ = result.data.questions.find(
                (q) => q.questionId === fv.id,
              );
              if (!uploadedQ) return fv;
              return {
                ...fv,
                value: uploadedQ.files.map((f) => f.url).join(', '),
                fileObjects: [],
              };
            }

            if (
              question.typeQuestion === 'Compuesta' &&
              fv.complexAnswers?.length > 0
            ) {
              const childFileQuestions = (
                question.childrenQuestions || []
              ).filter((c) => c.typeQuestion === 'Archivos');
              if (childFileQuestions.length === 0) return fv;

              const updatedComplexAnswers = fv.complexAnswers.map((ca) => {
                const updatedChildAnswers = { ...ca.selectedChildAnswers };
                childFileQuestions.forEach((child) => {
                  const match = result.data.questions.find(
                    (q) => String(q.questionId) === String(child.questionId),
                  );
                  if (match) {
                    updatedChildAnswers[child.questionId] = match.files
                      .map((f) => f.url)
                      .join(', ');
                  }
                });
                return { ...ca, selectedChildAnswers: updatedChildAnswers };
              });

              return { ...fv, complexAnswers: updatedComplexAnswers };
            }

            return fv;
          });
        }
        setIsUploadingFiles(false);
        onAnswered(updatedFormValues);
        handleNextAnswer();
      } catch (error) {
        setIsUploadingFiles(false);
        console.error('❌ Error al subir archivos:', error);

        // ✅ Error general con notistack
        enqueueSnackbar(
          error.message ||
            'Error al subir archivos. Por favor, intenta nuevamente.',
          {
            variant: 'error',
            autoHideDuration: 5000,
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
          },
        );

        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      console.error('❌ Validación falló, no se puede finalizar');
    }
  };

  const { handleBack } = useNavigationHandler({
    nameStep,
    activeStepper,
    activeDemographicStep,
    activeStep,
    totalDemographicSteps,
    handlePreviousAnswer,
    setActiveDemographicStep,
    setActiveStep,
    setSubmitAttempted,
    setDemographicSelections,
  });

  const handleChangeSlider = (event, newValue, index) => {
    if (newValue === null) return; // <- Seguridad, si deselecciona todo

    setFormValues((prevFormValues) => {
      const newFormValues = [...prevFormValues];
      newFormValues[index].value = newValue.toString();
      newFormValues[index].nameQuestion = questions[index].questionName;
      return newFormValues;
    });

    setUnansweredQuestions((prevUnanswered) =>
      prevUnanswered.filter((unansweredIndex) => unansweredIndex !== index),
    );

    setValues((prevValues) => ({
      ...prevValues,
      [index]: newValue, // 👈 Aquí debes usar newValue, no event.target.value
    }));
  };

  const handleChangeDate = (newValue, index) => {
    if (!newValue) return;

    setFormValues((prevFormValues) => {
      const newFormValues = [...prevFormValues];
      newFormValues[index].value = newValue.format('YYYY-MM-DD'); // formato a string
      newFormValues[index].nameQuestion = questions[index].questionName;
      return newFormValues;
    });

    setUnansweredQuestions((prevUnanswered) =>
      prevUnanswered.filter((unansweredIndex) => unansweredIndex !== index),
    );

    setValues((prevValues) => ({
      ...prevValues,
      [index]: newValue,
    }));
  };

  const marks = [];

  for (let i = 0; i <= 10; i++) {
    marks.push({
      value: i,
      label: `${i}`,
    });
  }

  function calculateValue(value) {
    return value; // Ahora no necesitamos el cálculo de la potencia, solo devuelve el valor tal cual
  }

  const {
    dictatingIndex,
    isRecording,
    recordingTime,
    usedRecordingSlots,
    resetCount,
    baseTextRef,
    pausedQuestions,
    startDictation,
    pauseDictation,
    resumeDictation,
    stopDictation,
    getMaxRecordingTime,
  } = useDictation({
    questions,
    formValues,
    setFormValues,
    browserSupportsSpeechRecognition,
    resetTranscript,
  });

  const sliderColor = (index) => {
    if (
      values[index] === undefined ||
      (values[index] >= 0 && values[index] <= 6)
    ) {
      return 'red';
    } else if (values[index] >= 7 && values[index] <= 8) {
      return 'yellow';
    } else {
      return 'green';
    }
  };

  // Función auxiliar para formatear el tiempo
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Función para obtener el color según el tiempo
  const getTimeColor = (seconds, maxTime) => {
    // ✅ Agregar parámetro maxTime
    const percentage = (seconds / maxTime) * 100;
    if (percentage < 66) return 'success.main';
    if (percentage < 83) return 'warning.main';
    return 'error.main';
  };

  // Función para verificar si puede grabar
  const canRecord = (idx) => {
    const currentResets = resetCount[idx] || 0;
    const timeExpired = usedRecordingSlots.has(idx);
    return !timeExpired && currentResets < MAX_RESETS;
  };

  // Función para obtener el mensaje del tooltip
  const getRecordingTooltip = (idx) => {
    const currentResets = resetCount[idx] || 0;
    const timeExpired = usedRecordingSlots.has(idx);
    const remainingResets = MAX_RESETS - currentResets;

    if (timeExpired) {
      return 'Tiempo de grabación agotado';
    }
    if (currentResets >= MAX_RESETS) {
      return 'Has usado todos los intentos permitidos';
    }
    if (currentResets > 0) {
      return `${remainingResets} ${
        remainingResets === 1 ? 'intento restante' : 'intentos restantes'
      }`;
    }
    return 'Iniciar dictado';
  };
  /**
   * Handles the change of the radio button.
   *
   * @param event
   * @param index
   */

  const handleRadioChange = (
    event,
    index,
    conditional,
    childrenQuestionNumber,
  ) => {
    const selectedValue = event.target.value;
    const currentQuestion = questions[index];
    const selectedOption = currentQuestion.options.find(
      (opt) => opt.optionName === selectedValue,
    );

    // 1. Guarda el valor real seleccionado
    setSelectedRadioOptions((prev) => ({
      ...prev,
      [currentQuestion.questionId]: selectedValue,
    }));

    // 2. EVALÚA LA CONDICIÓN PRIMERO con el valor real
    if (conditional) {
      updateVisibility(index, childrenQuestionNumber, selectedValue);
    }

    // 3. Después maneja "Otro" vs opciones normales
    if (selectedOption?.isOther) {
      // ✅ CAMBIO: Guardar el optionName de "Otro", NO dejarlo vacío
      setFormValues((prev) => {
        const updated = [...prev];
        updated[index].value = selectedValue; // ← Guarda "Otro" aquí
        updated[index].nameQuestion = currentQuestion.questionName;
        return updated;
      });

      // Inicializa otherValue vacío (el usuario lo llenará después)
      setOtherOptionValues((prev) => ({
        ...prev,
        [currentQuestion.questionId]: '',
      }));

      // ✅ NO marcar como respondida aún, falta llenar el campo de texto
      // Por eso NO filtramos unansweredQuestions aquí
    } else {
      // Opción normal
      setFormValues((prev) => {
        const updated = [...prev];
        updated[index].value = selectedValue;
        updated[index].nameQuestion = currentQuestion.questionName;
        return updated;
      });

      // Limpiar otherValue si existía
      setOtherOptionValues((prev) => {
        const updated = { ...prev };
        delete updated[currentQuestion.questionId];
        return updated;
      });

      // ✅ Marcar como respondida solo si NO es "Otro"
      setUnansweredQuestions((prevUnanswered) =>
        prevUnanswered.filter((unansweredIndex) => unansweredIndex !== index),
      );
    }
  };

  // SOLUCIÓN 2: Modificar updateVisibility para aceptar un valor directo
  const updateVisibility = (
    index,
    childrenQuestionNumber,
    directValue = null,
  ) => {
    const newVisibleQuestions = [...visibleQuestions];
    const currentQuestion = questions[index];

    // Usa el valor directo si se proporciona, si no usa formValuesRef
    const selectedAnswer = directValue || formValuesRef.current[index]?.value;

    const questionNumber =
      Number.parseInt(childrenQuestionNumber) === 0
        ? questions.length + 1
        : childrenQuestionNumber;

    if (Number.parseInt(childrenQuestionNumber) === 0) {
      setIsZeroIndexActive(true);
    } else {
      setIsZeroIndexActive(false);
    }

    // 1. Obtener todas las hijas de la opción seleccionada
    const validChildren =
      currentQuestion.options
        ?.filter((opt) => opt.optionName === selectedAnswer)
        .map((opt) => Number.parseInt(opt.childrenQuestionNumber)) || [];

    // 2. Para cada hija encontrada, mostrar el rango hasta la siguiente madre o fin
    validChildren.forEach((childNumber) => {
      const startIndex = questions.findIndex(
        (q) => q.questionNumber === childNumber,
      );
      if (startIndex !== -1) {
        for (let i = startIndex; i < questions.length; i++) {
          const q = questions[i];
          newVisibleQuestions[i] = true;

          const esOtraMadre = (q.options || []).some(
            (opt) => opt.childrenQuestionNumber,
          );
          if (i !== startIndex && esOtraMadre) break;
        }
      }
    });

    for (let k = index + 1; k < questionNumber - 1; k++) {
      newVisibleQuestions[k] = false;
    }

    // 3. Ocultar lo que no se debe mostrar
    for (let i = 0; i < questions.length; i++) {
      if (!newVisibleQuestions[i]) {
        formValuesRef.current[i] = initializeFormValue(questions[i]);
      }
    }

    setFormValues([...formValuesRef.current]);
    setVisibleQuestions(newVisibleQuestions);
  };
  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  useEffect(() => {
    if (isZeroIndexActive) {
      if (verifyCurrentStepAnswersSelected()) {
        handleNextAnswer();
      }
    }
  }, [visibleQuestions]);

  const verifyCurrentStepAnswersSelected = () => {
    const currentStepAnswers = formValues.slice(
      activeStep * pagination,
      (activeStep + 1) * pagination,
    );
    const unansweredIndexes = [];

    for (let i = 0; i < currentStepAnswers.length; i++) {
      const globalIndex = i + activeStep * pagination;
      const formValue = currentStepAnswers[i];
      const question = questions[globalIndex]; // ← AGREGAR ESTA LÍNEA

      if (!question.required) continue;
      if (!visibleQuestions[globalIndex]) continue;
      if (
        isInformativeText(question?.typeQuestion) ||
        isMultimedia(question?.typeQuestion)
      )
        continue; // ← USAR question.typeQuestion

      let isUnanswered = false;
      if (question?.typeQuestion === 'Archivos') {
        const files = formValue.fileObjects; // ✅
        if (!files || !Array.isArray(files) || files.length === 0) {
          isUnanswered = true;
        }
      } else if (isBipolarSlider(question?.typeQuestion)) {
        // Verificar si el slider no ha sido movido (permanece en 0)
        const sliderValue = values[globalIndex];

        // Si no existe valor en el estado 'values' significa que no se ha tocado
        // O si existe pero es 0 y no se ha interactuado con él
        if (sliderValue === undefined || sliderValue === null) {
          isUnanswered = true;
        }
        // Si queremos que 0 también sea una respuesta válida una vez que se haya movido,
        // podríamos agregar un estado adicional para rastrear la interacción
        // Cambiar todas las referencias de formValue.questionType a question.typeQuestion
      } else if (isBigSelection(question?.typeQuestion)) {
        // ✅ Si settings es null O limitOptions es 1, validar .value (modo único)
        if (
          !question?.settings ||
          (question?.settings?.haveLimit === true &&
            question?.settings?.limitOptions === 1)
        ) {
          isUnanswered = !formValue.value || formValue.value === '';
        }
        // Si es múltiple (limitOptions > 1 o ilimitado), validar .values
        else {
          const hasAnySelected = Object.values(formValue.values || {}).some(
            (val) => val === true,
          );
          isUnanswered = !hasAnySelected;
        }
      } else if (question?.typeQuestion === 'Opción Múltiple') {
        // Validar si hay al menos una opción seleccionada
        const hasAnySelected = Object.values(formValue.values).some(
          (val) =>
            val === true || (typeof val === 'string' && val.trim() !== ''),
        );
        isUnanswered = !hasAnySelected;

        // Validar campo "Otro" si está seleccionado y vacío
        const otherOption = question.options.find((opt) => opt.isOther);
        if (otherOption && hasOtherSelected(question, formValue)) {
          const otherText = otherOptionValues[question.questionId];
          if (!otherText || otherText.trim() === '') {
            isUnanswered = true; // Marcar como no respondida si el campo "Otro" está vacío
          }
        }
      } else if (question?.typeQuestion === 'Relacional') {
        isUnanswered =
          formValue.selectionValues === null ||
          Object.keys(formValue.selectionValues).length === 0 ||
          Object.values(formValue.selectionValues).some(
            (val) => val === '' || val === null,
          );
      } else if (isConstantAdd(question?.typeQuestion)) {
        const sum = Object.values(formValue.sliders || {}).reduce(
          (a, b) => a + b,
          0,
        );
        const maxScore = question?.score ?? 100;
        isUnanswered = sum !== maxScore;
        /*if (question?.autoValidate) {
          isUnanswered = sum !== maxScore;
        } else {
          //isUnanswered = sum > maxScore;
          isUnanswered = sum === 0 || sum > maxScore;
        }*/
      } else if (isMatrix(question?.typeQuestion)) {
        // ← AGREGAR VALIDACIÓN ESPECÍFICA PARA MATRIZ
        const isValid = matrizRefs.current[question.questionId]?.validate?.();
        isUnanswered = !isValid;
      } else if (isComplex(question?.typeQuestion)) {
        // Validación para preguntas compuestas
        const complexRef = questionRefs[question.questionId]?.current;
        if (complexRef && complexRef.hasErrors && complexRef.hasErrors()) {
          isUnanswered = true;
        } else if (
          !formValue.complexAnswers ||
          formValue.complexAnswers.length === 0
        ) {
          isUnanswered = true;
        }
      } else if (isTable(question?.typeQuestion)) {
        const tableRef = questionRefs[question.questionId]?.current;

        if (tableRef) {
          // Validar usando el ref del componente
          const errors = tableRef.validate?.();

          if (errors && errors.length > 0) {
            isUnanswered = true;
          }
        } else {
          // Si no hay ref, verificar si hay al menos una fila con datos
          const tableData = formValue.value || [];

          if (!Array.isArray(tableData) || tableData.length === 0) {
            isUnanswered = true;
          } else {
            // Verificar si hay al menos una celda con datos
            const hasAnyData = tableData.some((row) => {
              return Object.values(row.data || {}).some(
                (cellValue) => cellValue && cellValue.trim() !== '',
              );
            });

            if (!hasAnyData) {
              isUnanswered = true;
            }
          }
        }
      } else {
        // Esto debe quitarse sólo se hizo para una encuesta de CMI
        if (question.questionId != 6852) {
          const selectedOption = question.options.find(
            (opt) => opt.optionName === formValue.value,
          );

          // Validar campo de texto para "Otro"
          if (selectedOption?.isOther) {
            const otherText = otherOptionValues[question.questionId];
            isUnanswered = !otherText || otherText.trim() === '';
          } else {
            isUnanswered = formValue.value === null || formValue.value === '';
          }
        }
      }

      if (isUnanswered) {
        unansweredIndexes.push(globalIndex);
      }
    }

    setUnansweredQuestions(unansweredIndexes);

    if (unansweredIndexes.length > 0) {
      const firstUnanswered = unansweredIndexes[0];
      const questionType = questions[firstUnanswered]?.typeQuestion;

      if (!isInformativeText(questionType)) {
        questionRefs[
          questions[firstUnanswered].questionId
        ]?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }

    return unansweredIndexes.length === 0;
  };

  /**
   * Handles the change of the checkbox.
   *
   * @param event
   * @param index
   */
  const handleCheckboxChange = (event, index) => {
    const isChecked = event.target.checked;
    const optionValue = event.target.value;
    const currentQuestion = questions[index];
    const questionId = currentQuestion.questionId;

    setFormValues((prevFormValues) => {
      const newFormValues = [...prevFormValues];
      const otherOption = currentQuestion.options.find((opt) => opt.isOther);

      newFormValues[index].nameQuestion = currentQuestion.questionName;

      if (otherOption && optionValue === otherOption.optionName) {
        // Este es el checkbox "Otro"
        newFormValues[index].values[otherOption.optionName] = isChecked;

        if (!isChecked) {
          // Limpiar el input y otherValue cuando se desmarca "Otro"
          setOtherOptionValues((prev) => ({
            ...prev,
            [questionId]: '',
          }));
          newFormValues[index].otherValue = '';
        }
      } else {
        // Comportamiento normal para checkboxes regulares
        newFormValues[index].values[optionValue] = isChecked;
      }

      // Remover de preguntas sin responder
      setUnansweredQuestions((prevUnanswered) =>
        prevUnanswered.filter((unansweredIndex) => unansweredIndex !== index),
      );
      return newFormValues;
    });
  };

  const handleOtherCheckboxInputChange = (e, questionId, index) => {
    const inputValue = e.target.value;
    const currentQuestion = questions[index];

    // Actualizar el estado del input
    setOtherOptionValues((prev) => ({
      ...prev,
      [questionId]: inputValue,
    }));

    // Actualizar formValues con el nuevo valor del input
    setFormValues((prevFormValues) => {
      const updated = [...prevFormValues];

      // ✅ Guardar el texto en otherValue, NO en values
      updated[index].otherValue = inputValue;
      updated[index].nameQuestion = currentQuestion.questionName;

      return updated;
    });
  };

  const hasOtherSelected = (question, formValue) => {
    const otherOption = question.options.find((opt) => opt.isOther);
    if (!otherOption) return false;

    // ✅ Verificar SOLO si está seleccionada por nombre original
    return formValue.values[otherOption.optionName] === true;
  };

  const handleSliderSumaConstante = (rawValue, questionIndex, optionName) => {
    const pregunta = questions[questionIndex];
    const maxScore = pregunta.score ?? 100;

    // Calcular applied AQUÍ afuera para usarlo en ambos setters
    const currentSliders = formValuesRef.current[questionIndex].sliders || {};
    const prevVal = currentSliders[optionName] || 0;
    const totalSinEsta =
      Object.values(currentSliders).reduce((a, b) => a + b, 0) - prevVal;

    let applied = rawValue;
    if (pregunta.autoValidate) {
      const espacioRestante = maxScore - totalSinEsta;
      applied = Math.max(0, Math.min(rawValue, espacioRestante));
    }

    setFormValues((prev) =>
      prev.map((fv, idx) => {
        if (idx !== questionIndex) return fv;
        const sliders = { ...fv.sliders };
        const values = { ...fv.values };
        sliders[optionName] = applied;
        values[optionName] = true;
        return { ...fv, sliders, values, nameQuestion: pregunta.questionName };
      }),
    );

    setUnansweredQuestions((prevU) => {
      const newSliders = { ...currentSliders };
      newSliders[optionName] = applied;
      const sum = Object.values(newSliders).reduce((a, b) => a + b, 0);
      const hasError = sum !== maxScore;
      const already = prevU.includes(questionIndex);
      if (hasError && !already) return [...prevU, questionIndex];
      if (!hasError && already) return prevU.filter((i) => i !== questionIndex);
      return prevU;
    });
  };
  /**
   * Handles the change of the checkbox.
   *
   * @param event
   * @param index
   */
  const handleRelationalChange = (newValue, index) => {
    setFormValues((prevFormValues) => {
      const newFormValues = [...prevFormValues];
      newFormValues[index].selectionValues = {
        ...newFormValues[index].selectionValues,
        ...newValue,
      };
      newFormValues[index].nameQuestion = questions[index].questionName;
      setUnansweredQuestions((prevUnanswered) =>
        prevUnanswered.filter((unansweredIndex) => unansweredIndex !== index),
      );
      return newFormValues;
    });
  };

  /**
   * Handles the change of the range.
   *
   * @param value
   * @param index
   */
  const handleRangeChange = (value, index) => {
    setFormValues((prevFormValues) => {
      const newFormValues = [...prevFormValues];

      newFormValues[index].value = value.toString();
      newFormValues[index].nameQuestion = questions[index].questionName;
      setUnansweredQuestions((prevUnanswered) =>
        prevUnanswered.filter((unansweredIndex) => unansweredIndex !== index),
      );

      return newFormValues;
    });
  };

  /**
   * Get likert icon.
   *
   * @param value
   * @returns {*|string}
   */
  const getLikertIcon = (value) => {
    switch (value.toLowerCase().replace(/\s\s+/g, ' ')) {
      case 'de acuerdo':
        return Deacuerdo;
      case 'en desacuerdo':
        return EnDesacuerdo;
      case 'ni de acuerdo ni en desacuerdo':
        return NiDeacuerdoNiEnDesacuerdo;
      case 'totalmente de acuerdo':
        return TotalmenteDeAcuerdo;
      case 'totalmente en desacuerdo':
        return TotalmenteEnDesacuerdo;
      default:
        return '';
    }
  };

  /**
   * Total of steps.
   *
   * @returns {number}
   */
  const totalOfSteps = () => {
    // ✅ Si estamos en demografía, contar SOLO pasos demográficos
    if (nameStep && nameStep[activeStepper] === 'Datos demográficos') {
      return totalDemographicSteps();
    }

    // ✅ Si estamos en preguntas, contar SOLO pasos de preguntas
    const totalOfQuestions = questions.length;
    return Math.ceil(totalOfQuestions / pagination);
  };

  useEffect(() => {
    // Establecer refs para cada pregunta en la primera renderización
    const newRefs = questions.reduce((acc, { questionId }) => {
      acc[questionId] = React.createRef();
      return acc;
    }, {});

    setQuestionRefs(newRefs);
  }, []);

  // watch changes in form values
  useEffect(() => {
    if (nameStep && nameStep[activeStepper] !== 'Datos demográficos') {
      sessionStorage.setItem('formValues', JSON.stringify(formValues));
    }
    onAnswered(formValues);
  }, [formValues, nameStep, activeStepper]);
  useEffect(() => {
    if (nameStep && nameStep[activeStepper] === 'Datos demográficos') {
      sessionStorage.setItem(
        'demographicSelections',
        JSON.stringify(demographicSelections),
      );
    }
  }, [demographicSelections, nameStep, activeStepper]);

  useEffect(() => {
    const childrenQuestionNumber = [];

    questions.map((question, index) => {
      if (question.conditional) {
        question.options.map((option) => {
          const childNumber = Number.parseInt(option.childrenQuestionNumber);
          // ✅ CAMBIO PRINCIPAL: Solo agregar si NO es 0
          if (
            childNumber !== 0 &&
            !childrenQuestionNumber.includes(childNumber)
          ) {
            childrenQuestionNumber.push(childNumber);
          }
        });
      }
    });

    const visibility = questions.map((question, index) => {
      // ✅ Ocultar si es una pregunta "hija" de una condicional
      if (childrenQuestionNumber.includes(question.questionNumber)) {
        return false;
      }

      // ✅ NUEVO: También ocultar preguntas que vienen después de condicionales
      // pero que no son "hijas" directas
      const firstConditionalIndex = questions.findIndex((q) => q.conditional);
      if (
        firstConditionalIndex !== -1 &&
        index > firstConditionalIndex &&
        !question.conditional
      ) {
        // Si no está en la lista de hijas pero viene después de una condicional, ocultarla
        return false;
      }

      return true;
    });

    setVisibleQuestions(visibility);
  }, [questions]);

  useEffect(() => {
    // ✅ Solo ejecutar si hay preguntas
    if (!questions || questions.length === 0) return;

    setFormValues((prevFormValues) => {
      // ✅ Si prevFormValues ya tiene datos válidos, no sobreescribir
      if (
        prevFormValues.length === questions.length &&
        prevFormValues.some(
          (fv) => fv.value || Object.keys(fv.values || {}).length > 0,
        )
      ) {
        return prevFormValues;
      }

      return prevFormValues.map((formValue, index) => {
        const question = questions[index];

        if (!question) return formValue; // ✅ Protección

        if (
          isBigSelection(question.typeQuestion) ||
          isComplex(question.typeQuestion)
        ) {
          // No agregamos selectionValues
          return formValue;
        }

        // Agregamos selectionValues si aplica
        return {
          ...formValue,
          selectionValues: question.options.reduce((acc, option) => {
            acc[option.optionName] = '';
            return acc;
          }, {}),
        };
      });
    });
  }, [questions]);

  useEffect(() => {
    // Encuentra la primera pregunta visible en el nuevo paso
    const startIndex = activeStep * pagination;
    const endIndex = (activeStep + 1) * pagination;
    for (let i = startIndex; i < endIndex; i++) {
      const question = questions[i];
      if (question && visibleQuestions[i]) {
        const ref = questionRefs[question.questionId];
        if (ref?.current) {
          ref.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          break;
        }
      }
    }
  }, [activeStep]);
  let firstUncheckedIndex = -1;
  // Estos bucles buscan el primer checkbox sin marcar
  for (let i = 0; i < formValues.length; i++) {
    const questionValues = formValues[i].values;
    const uncheckedOptions = Object.keys(questionValues).filter(
      (optionName) => !questionValues[optionName],
    );

    if (uncheckedOptions.length > 0) {
      firstUncheckedIndex = i;
      break;
    }
  }

  const isFirstVisibleQuestionInGroup = (
    index,
    activeStep,
    visibleQuestions,
  ) => {
    const groupStart = activeStep * pagination;
    // Recorremos desde el inicio del grupo hasta el índice actual (excluyéndolo)
    for (let i = groupStart; i < index; i++) {
      if (visibleQuestions[i]) {
        // Si encontramos una pregunta visible antes del índice actual,
        // entonces este no es el primero visible.
        return false;
      }
    }
    // Si la pregunta actual es visible, y no se encontró ninguna visible antes en el grupo,
    // es la primera visible.overflowY
    return visibleQuestions[index];
  };

  const handleComplexAnswerChange = (answers, index) => {
    const question = questions[index]; // ✅ obtener la pregunta actual
    setFormValues((prevFormValues) => {
      const newFormValues = [...prevFormValues];
      newFormValues[index].complexAnswers = answers;
      if (!isMultimedia(question.typeQuestion)) {
        newFormValues[index].nameQuestion = question.questionName;
      }

      // Remover de preguntas sin responder si hay respuestas válidas
      const hasValidAnswers = answers.some(
        (answer) =>
          answer.optionName &&
          (question.childrenQuestions.length === 0 ||
            Object.keys(answer.selectedChildAnswers).length > 0),
      );

      if (hasValidAnswers) {
        setUnansweredQuestions((prevUnanswered) =>
          prevUnanswered.filter((unansweredIndex) => unansweredIndex !== index),
        );
      }

      return newFormValues;
    });
  };

  const mapPausedDataToFormValues = (pausedData) => {
    return questions.map((question, index) => {
      const pausedAnswer = pausedData.answers.find(
        (answer) => answer.id === question.questionId,
      );

      if (!pausedAnswer) {
        return initializeFormValue(question);
      }

      const base = {
        id: question.questionId,
        questionType: question.typeQuestion,
        value: pausedAnswer.value || '',
        values: pausedAnswer.values || {},
        selectionValues: pausedAnswer.selectionValues || {},
      };

      // Casos especiales por tipo de pregunta
      if (isConstantAdd(question.typeQuestion)) {
        return {
          ...base,
          sliders: pausedAnswer.sliders || {},
          values: pausedAnswer.values || {},
        };
      }

      if (isComplex(question.typeQuestion)) {
        return {
          ...base,
          complexAnswers: pausedAnswer.complexAnswers || [],
        };
      }

      if (isMatrix(question.typeQuestion)) {
        return {
          ...base,
          selectionValuesMatriz: pausedAnswer.selectionValuesMatriz || null,
        };
      }

      // Para preguntas de opción múltiple (checkbox)
      if (isCheckbox(question.typeQuestion)) {
        return {
          ...base,
          values: pausedAnswer.values || {},
        };
      }

      // Para preguntas relacionales
      if (isRelational(question.typeQuestion)) {
        return {
          ...base,
          selectionValues: pausedAnswer.selectionValues || {},
        };
      }

      return base;
    });
  };
  const simulateUpdateVisibility = (index, selectedAnswer, currentVisible) => {
    const newVisible = [...currentVisible];
    const currentQuestion = questions[index];
    if (!currentQuestion) return newVisible;

    // Opción seleccionada
    const selectedOption = (currentQuestion.options || []).find(
      (opt) => opt.optionName === selectedAnswer,
    );

    const childrenQuestionNumber = selectedOption
      ? Number.parseInt(selectedOption.childrenQuestionNumber)
      : NaN;

    const questionNumber =
      !selectedOption ||
      isNaN(childrenQuestionNumber) ||
      childrenQuestionNumber === 0
        ? questions.length + 1
        : childrenQuestionNumber;

    // Hijos válidos (incluye 0, pero 0 no tendrá match en questionNumber)
    const validChildren =
      (currentQuestion.options || [])
        .filter((opt) => opt.optionName === selectedAnswer)
        .map((opt) => Number.parseInt(opt.childrenQuestionNumber)) || [];

    // Mostrar hijos y bloque hasta la siguiente "madre"
    validChildren.forEach((childNumber) => {
      const startIndex = questions.findIndex(
        (q) => q.questionNumber === childNumber,
      );
      if (startIndex === -1) return;

      for (let i = startIndex; i < questions.length; i++) {
        const q = questions[i];
        newVisible[i] = true;

        // 👉 MISMA LÓGICA QUE EN updateVisibility
        const esOtraMadre = (q.options || []).some(
          (opt) => opt.childrenQuestionNumber,
        );
        if (i !== startIndex && esOtraMadre) break;
      }
    });

    // Ocultar lo que hay entre la madre y el primer hijo (o hasta el final si children = 0)
    for (let k = index + 1; k < questionNumber - 1; k++) {
      newVisible[k] = false;
    }

    return newVisible;
  };
  const recomputeVisibilityFromAnswers = (restored) => {
    if (!questions || questions.length === 0) return;

    // 1) Construir lista de números de pregunta hijas (igual que en el useEffect inicial)
    const childrenQuestionNumbers = [];

    questions.forEach((question) => {
      if (!question.conditional) return;

      (question.options || []).forEach((opt) => {
        const childNumber = Number.parseInt(opt.childrenQuestionNumber);
        if (
          !isNaN(childNumber) &&
          childNumber !== 0 &&
          !childrenQuestionNumbers.includes(childNumber)
        ) {
          childrenQuestionNumbers.push(childNumber);
        }
      });
    });

    const firstConditionalIndex = questions.findIndex((q) => q.conditional);

    // 2) Visibilidad base: igual que tu efecto de inicialización
    let newVisible = questions.map((question, index) => {
      // Ocultar si es hija
      if (childrenQuestionNumbers.includes(question.questionNumber)) {
        return false;
      }

      // Ocultar todo lo que viene después de la primera condicional
      // y no sea condicional
      if (
        firstConditionalIndex !== -1 &&
        index > firstConditionalIndex &&
        !question.conditional
      ) {
        return false;
      }

      return true;
    });

    // 3) Aplicar la lógica condicional simulando updateVisibility
    questions.forEach((question, index) => {
      if (!question.conditional) return;

      const saved = restored[index];
      const selectedAnswer =
        saved && typeof saved.value === 'string' ? saved.value : '';

      if (!selectedAnswer) return;

      newVisible = simulateUpdateVisibility(index, selectedAnswer, newVisible);
    });

    setVisibleQuestions(newVisible);
  };

  useEffect(() => {
    // Solo aplica cuando estamos en el step de ENCUESTA
    if (!nameStep || nameStep[activeStepper] === 'Datos demográficos') return;
    if (!questions || questions.length === 0) return;

    const currentAnswers = formValuesRef.current || [];

    const hasAnyAnswer = currentAnswers.some(
      (fv) =>
        fv &&
        (fv.value ||
          (fv.values && Object.values(fv.values || {}).some(Boolean)) ||
          (fv.sliders && Object.values(fv.sliders || {}).some((v) => v > 0)) ||
          (fv.complexAnswers && fv.complexAnswers.length > 0)),
    );

    if (!hasAnyAnswer) return;

    // 🔁 Reconstruye visibilidad a partir de las respuestas actuales
    recomputeVisibilityFromAnswers(currentAnswers);
  }, [nameStep, activeStepper, questions]);

  const { isPausedDataLoaded, loadError } = usePausedDataLoader({
    data,
    questions,
    mapPausedDataToFormValues,
    recomputeVisibilityFromAnswers,
    setFormValues,
    setOtherOptionValues,
    setSelectedRadioOptions,
    setValues,
  });

  useEffect(() => {
    if (loadError) {
      console.error('Error al cargar datos pausados:', loadError);
      // Aquí podrías mostrar un snackbar o notificación al usuario
    }
  }, [loadError]);

  useEffect(() => {
    if (dictatingIndex == null || !transcript) return;

    // Si es pregunta hija (índice es string tipo "uuid-childId"), lo maneja ComplexQuestion
    const isChildQuestion = isNaN(Number(dictatingIndex));
    if (isChildQuestion) return;

    const base = baseTextRef.current || '';
    const merged =
      base && transcript ? `${base} ${transcript}` : `${base}${transcript}`;

    const idx = Number(dictatingIndex);
    setFormValues((prev) => {
      const updated = [...prev];
      if (updated[idx]) {
        updated[idx] = {
          ...updated[idx],
          value: merged,
          nameQuestion: questions[idx]?.questionName,
        };
      }
      return updated;
    });
  }, [transcript]);

  return (
    <div className={styles.SurveyForm} style={{ '--survey-font': fontFamily }}>
      {activeStep === 0 && (
        <SurveyDescription
          description={descriptionSurvey}
          maxLength={150}
          primaryColor={primaryColor}
          isMobile={isMobile}
          showOnlyOnFirstStep={true}
          currentStep={activeStep}
        />
      )}
      {nameStep && nameStep[activeStepper] === 'Datos demográficos' ? (
        <DemographicSection
          demographicData={normalizedDemographicData}
          demographicSelections={demographicSelections}
          activeDemographicStep={activeDemographicStep}
          totalDemographicSteps={totalDemographicSteps()}
          pagination={pagination}
          submitAttempted={submitAttempted}
          primaryColor={primaryColor}
          onSelectionChange={(selections) => {
            setDemographicSelections(selections);
          }}
        />
      ) : (
        <>
          {questions.map((question, index) => (
            <QuestionRenderer
              key={question.questionId} // ✅ CORRECTO
              question={question} // ✅ CORRECTO
              index={index}
              activeStep={activeStep}
              pagination={pagination}
              visibleQuestions={visibleQuestions}
              isFirstVisibleQuestionInGroup={isFirstVisibleQuestionInGroup}
              // Estados
              formValues={formValues}
              unansweredQuestions={unansweredQuestions}
              submitAttempted={submitAttempted}
              otherOptionValues={otherOptionValues}
              selectedRadioOptions={selectedRadioOptions}
              values={values}
              sliderErrors={sliderErrors}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              questionRefs={questionRefs}
              matrizRefs={matrizRefs}
              pausedQuestions={pausedQuestions}
              // Handlers
              handleRadioChange={handleRadioChange}
              handleCheckboxChange={handleCheckboxChange}
              handleRangeChange={handleRangeChange}
              handleChangeSlider={handleChangeSlider}
              handleChangeDate={handleChangeDate}
              handleRelationalChange={handleRelationalChange}
              handleSliderSumaConstante={handleSliderSumaConstante}
              handleComplexAnswerChange={handleComplexAnswerChange}
              handleOtherCheckboxInputChange={handleOtherCheckboxInputChange}
              updateVisibility={updateVisibility}
              setFormValues={setFormValues}
              setUnansweredQuestions={setUnansweredQuestions}
              setOtherOptionValues={setOtherOptionValues}
              setSliderErrors={setSliderErrors}
              // Funciones auxiliares
              getLikertIcon={getLikertIcon}
              calculateValue={calculateValue}
              sliderColor={sliderColor}
              marks={marks}
              textBipolarBar={question.textBipolarBar}
              // Voice recognition
              browserSupportsSpeechRecognition={
                browserSupportsSpeechRecognition
              }
              dictatingIndex={dictatingIndex}
              isRecording={isRecording}
              recordingTime={recordingTime}
              resetCount={resetCount}
              MAX_RESETS={MAX_RESETS}
              startDictation={startDictation}
              pauseDictation={pauseDictation}
              resumeDictation={resumeDictation}
              stopDictation={stopDictation}
              transcript={transcript}
              baseTextRef={baseTextRef}
              canRecord={canRecord}
              getMaxRecordingTime={getMaxRecordingTime}
              getRecordingTooltip={getRecordingTooltip}
              formatTime={formatTime}
              getTimeColor={getTimeColor}
              showUploadError={
                submitAttempted && unansweredQuestions.includes(index)
              } // ✅ AGREGAR ESTA LÍNEA
              fontFamily={fontFamily}
              hasOwnBold={hasOwnBold}
            />
          ))}
        </>
      )}

      <SurveyNavigation
        activeStep={activeStep}
        activeStepper={activeStepper}
        activeDemographicStep={activeDemographicStep}
        totalSteps={totalOfSteps()}
        totalDemographicSteps={totalDemographicSteps()}
        nameStep={nameStep}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        onNext={handleNext}
        onBack={handleBack}
      />
      {isSaveOption && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <PauseButton
            onPause={() => saveSurvey()}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          />
        </div>
      )}
    </div>
  );
};

export default SurveyForm;
